import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabase";
import AdminVideos from "./AdminVideos";
import AdminLinks from "./AdminLinks";
import AdminNewLink from "./AdminNewLink";

type Tab = "videos" | "links" | "new";

const TABS: { id: Tab; label: string }[] = [
  { id: "videos", label: "Videos" },
  { id: "links", label: "Links" },
  { id: "new", label: "New link" },
];

export default function Admin() {
  const nav = useNavigate();
  const loc = useLocation();
  const initial = (loc.hash.replace("#", "") as Tab) || "videos";
  const [tab, setTab] = useState<Tab>(
    TABS.some((t) => t.id === initial) ? initial : "videos"
  );

  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav("/admin/login", { replace: true });
      else setUserEmail(data.session.user.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session) nav("/admin/login", { replace: true });
      else setUserEmail(session.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, [nav]);

  const selectTab = (t: Tab) => {
    setTab(t);
    window.location.hash = `#${t}`;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-stone bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 md:py-4">
          {/* Row 1: logo + user — always single row */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight">VideoLink</h1>
              {import.meta.env.VITE_ENV_NAME === "staging" && (
                <span className="text-[10px] font-bold uppercase tracking-widest bg-lemonade text-black px-2 py-0.5 rounded">
                  Test
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm min-w-0">
              {userEmail && (
                <span className="text-concrete truncate hidden sm:inline">
                  {userEmail}
                </span>
              )}
              <button
                onClick={logout}
                className="text-concrete hover:text-charcoal whitespace-nowrap"
              >
                Sign out
              </button>
            </div>
          </div>

          {/* Row 2: tabs — full width, equal flex on mobile */}
          <nav className="mt-3 flex gap-1 -mb-px">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => selectTab(t.id)}
                className={`flex-1 sm:flex-initial px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition ${
                  tab === t.id
                    ? "bg-charcoal text-ivory"
                    : "text-concrete hover:text-charcoal hover:bg-ivory"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6">
        {tab === "videos" && <AdminVideos />}
        {tab === "links" && <AdminLinks onCreateNew={() => selectTab("new")} />}
        {tab === "new" && (
          <AdminNewLink onCreated={() => selectTab("links")} />
        )}
      </main>
    </div>
  );
}
